import React from 'react'
import { useTranslation } from 'react-i18next'
import {Modal, Space, Button} from 'antd'
import {observer} from 'mobx-react'

import {BankAccountDetails} from '@/pages/ChangeBankAccountDetails/components/BankAccountDetails'

import {BankChangeModalsProps} from './props'
import {GoogleManagerTagClassNames} from '@/constants'
import {
    BANK_CHANGE_MODAL,
    CHANGE_CANCELLED,
    CONFIRM_CHANGE
} from '@/constants/playwright-ids'

export const BankChangeModals: React.FC<BankChangeModalsProps> = observer(
    ({currentAccount, selectedAccount, accountType, confirmModal, successModal, errorModal}) => {
        const { t } = useTranslation()
        const confirmButtonTag =
            accountType === 'all'
                ? GoogleManagerTagClassNames.settings.outlets.changeBankDetails.all.modal.confirm
                : accountType === 'settlement'
                  ? GoogleManagerTagClassNames.settings.outlets.changeBankDetails.settlements.modal
                        .confirm
                  : GoogleManagerTagClassNames.settings.outlets.changeBankDetails.directDebit.modal
                        .confirm

        const cancelButtonTag =
            accountType === 'all'
                ? GoogleManagerTagClassNames.settings.outlets.changeBankDetails.all.modal.cancel
                : accountType === 'settlement'
                  ? GoogleManagerTagClassNames.settings.outlets.changeBankDetails.settlements.modal
                        .cancel
                  : GoogleManagerTagClassNames.settings.outlets.changeBankDetails.directDebit.modal
                        .cancel

        return (
            <>
                {selectedAccount && currentAccount && (
                    <Modal
                        title={t('ChangeBankAccountDetails.titles.bankChangeConfirmation')}
                        open={confirmModal.isOpen}
                        onCancel={confirmModal.onClose}
                        footer={
                            <Space>
                                <Button
                                    loading={confirmModal.isLoading}
                                    type='primary'
                                    onClick={confirmModal.onConfirm}
                                    className={confirmButtonTag}
                                    data-test-id={CONFIRM_CHANGE}
                                >
                                    <span className={confirmButtonTag}>
                                        {t('ChangeBankAccountDetails.buttons.confirm')}
                                    </span>
                                </Button>
                                <Button
                                    className={cancelButtonTag}
                                    onClick={confirmModal.onClose}
                                    data-test-id={CHANGE_CANCELLED}
                                >
                                    <span className={cancelButtonTag}>
                                        {t('ChangeBankAccountDetails.buttons.cancel')}
                                    </span>
                                </Button>
                            </Space>
                        }
                        data-test-id={BANK_CHANGE_MODAL}
                    >
                        <p>
                            {accountType === 'all'
                                ? t('ChangeBankAccountDetails.labels.youAreAboutToUpdateYourSettlementDirectDebitBankAccount')
                                : accountType === 'settlement'
                                  ? t('ChangeBankAccountDetails.labels.youAreAboutToUpdateYourSettlementBankAccount')
                                  : t('ChangeBankAccountDetails.labels.youAreAboutToUpdateYourDirectDebitBankAccount')
                            }
                        </p>
                        <BankAccountDetails
                            accountNumber={currentAccount.bankAccountNumber}
                            sortCode={currentAccount.sortCode}
                            label={t('ChangeBankAccountDetails.labels.currentBankAccountDetails')}
                        />
                        <br />
                        <br />
                        <BankAccountDetails
                            accountNumber={selectedAccount.bankDetail?.bankAccountNumber || ''}
                            sortCode={selectedAccount.bankDetail?.sortCode || ''}
                            label={t('ChangeBankAccountDetails.labels.newBankAccountDetails')}
                        />
                        <br />
                        <p>{t('ChangeBankAccountDetails.labels.byConfirmingYouAuthoriseChange')}</p>
                    </Modal>
                )}

                <Modal
                    title={t('ChangeBankAccountDetails.titles.bankChangeConfirmation')}
                    open={successModal.isOpen}
                    onCancel={successModal.onClose}
                    footer={
                        <Button onClick={successModal.onClose}>{t('ChangeBankAccountDetails.buttons.ok')}</Button>
                    }
                >
                    <p>{t('ChangeBankAccountDetails.labels.bankAccountChangeSubmitted')}</p>
                    <p>
                        {accountType === 'all'
                            ? t('ChangeBankAccountDetails.labels.accountWillBeUpdatedForOutlets')
                            : accountType === 'settlement'
                              ? t('ChangeBankAccountDetails.labels.accountWillBeUpdatedForSettlementOutlets')
                              : t('ChangeBankAccountDetails.labels.accountWillBeUpdatedForDirectDebitOutlets')}
                    </p>
                </Modal>

                <Modal
                    title={t('ChangeBankAccountDetails.titles.bankChangeFailed')}
                    open={errorModal.isOpen}
                    onCancel={errorModal.onClose}
                    footer={
                        <Button onClick={errorModal.onClose}>{t('ChangeBankAccountDetails.buttons.ok')}</Button>
                    }
                >
                    <p>{t('ChangeBankAccountDetails.labels.couldNotUpdateBankAccount')}</p>
                    <span>{t('ChangeBankAccountDetails.labels.nextSteps')}</span>
                    <br />
                    <ul>
                        <li>{t('ChangeBankAccountDetails.labels.tryAgain')}</li>
                        <li>{t('ChangeBankAccountDetails.labels.contactSupport')}</li>
                    </ul>
                </Modal>
            </>
        )
    }
)
